// Робот на экране ожидания (Алексей 26.10 #2635): прилетает из-за края, летает туда-сюда, иногда
// бьётся лбом в экран; когда ждать больше не надо (leaving) — улетает за ближний край, а не исчезает.
// Те же части и позы, что у робота «всё в норме» на дашборде смены (pages/Usage/robotRig).
import { useEffect, useRef } from 'react';
import {
  RobotParts, makeRig, blinkAt, bonkPose, type BotPose,
  RIG_AR, RIG_FH, RIG_PAD, RIG_BODY_L, RIG_BODY_R, RIG_BODY_C,
} from '@/pages/Usage/robotRig';
import { cn } from '@/lib/utils';

const IN_MS = 1300, OUT_MS = 1000;
const smooth = (u: number) => u * u * (3 - 2 * u);
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

export function WaitingRobot({ height, leaving, onGone, className }: {
  /** Высота полосы робота, px. */
  height: number;
  leaving?: boolean;
  /** Улетел за край (или сразу — при reduced motion). */
  onGone?: () => void;
  className?: string;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const botRef = useRef<HTMLDivElement>(null);
  const leavingRef = useRef(!!leaving);
  leavingRef.current = !!leaving;
  const onGoneRef = useRef(onGone);
  onGoneRef.current = onGone;
  const fw = Math.round(height * RIG_AR);
  const reduce = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // Без анимации улетать некому — сразу «улетел».
  useEffect(() => { if (reduce && leaving) onGoneRef.current?.(); }, [reduce, leaving]);

  useEffect(() => {
    const box = boxRef.current, bot = botRef.current;
    if (!box || !bot) return;
    const rig = makeRig(bot);
    const draw = (x: number, y: number, mirror: boolean, p: BotPose = {}, now = performance.now()) => {
      const k = p.k ?? 1;
      bot.style.transform = `translate(${x + (p.dx ?? 0)}px, ${y + (p.dy ?? 0) - height * RIG_PAD}px) scale(${(mirror ? -1 : 1) * k * (p.sx ?? 1)}, ${k * (p.sy ?? 1)})`;
      rig(p, now);
    };
    const bounds = () => {
      const W = box.clientWidth;
      const minX = -RIG_BODY_L * fw + 4;
      return { W, minX, maxX: Math.max(minX, W - RIG_BODY_R * fw - 4) };
    };
    const offRight = () => bounds().W - RIG_BODY_L * fw + 12;   // корпус целиком за правым краем
    const offLeft = () => -RIG_BODY_R * fw - 12;                 // … за левым
    const y0 = Math.max(0, (box.clientHeight - height) / 2);

    if (reduce) {
      const { minX, maxX } = bounds();
      draw((minX + maxX) / 2, y0, false);
      return;
    }

    type St = { kind: 'hover'; until: number; nextBlink: number; blinkEnd: number }
      | { kind: 'fly'; t0: number; ms: number; from: number; to: number; out?: boolean }
      | { kind: 'bonk'; t0: number };
    const startHover = (now: number): St => ({ kind: 'hover', until: now + rnd(1400, 3200), nextBlink: now + rnd(600, 1600), blinkEnd: 0 });
    let { minX, maxX } = bounds();
    let x = offRight();
    let mirror = true; // летит влево — смотрит влево
    let st: St = { kind: 'fly', t0: performance.now(), ms: IN_MS, from: x, to: minX + (maxX - minX) * rnd(0.45, 0.7) };
    let hovers = 0, gone = false, raf = 0;

    const tick = (now: number) => {
      ({ minX, maxX } = bounds());
      const bob = y0 + Math.sin(now / 1800 * 2 * Math.PI) * height * 0.04;
      const idleTilt = Math.sin(now / 2300 * 2 * Math.PI) * 2.5;
      // Пора улетать: из любого состояния (кроме уже начатого вылета) — к ближнему краю.
      if (leavingRef.current && !(st.kind === 'fly' && st.out)) {
        const toRight = x > (minX + maxX) / 2;
        const to = toRight ? offRight() : offLeft();
        mirror = !toRight;
        st = { kind: 'fly', t0: now, ms: OUT_MS, from: x, to, out: true };
      }
      if (st.kind === 'fly') {
        const u = Math.max(0, Math.min(1, (now - st.t0) / st.ms));
        // Вылет — разгоном (сразу прочь), прилёт и перелёты — плавно.
        const e = st.out ? u * u : smooth(u);
        x = st.from + (st.to - st.from) * e;
        const v = st.out ? Math.min(1, u * 1.6) : 6 * u * (1 - u) / 1.5;
        draw(x, bob, mirror, { speed: v, tilt: 12 * v + idleTilt * (1 - v), flame: 1 + 0.12 * v }, now);
        if (u >= 1) {
          if (st.out) {
            if (!gone) { gone = true; onGoneRef.current?.(); }
            return; // за краем — кадры больше не нужны
          }
          st = startHover(now);
        }
      } else if (st.kind === 'bonk') {
        const pose = bonkPose(now - st.t0, height);
        if (pose) draw(x, bob, mirror, { ...pose, tilt: (pose.tilt ?? 0) + idleTilt * 0.3 }, now);
        else { st = startHover(now); draw(x, bob, mirror, { tilt: idleTilt }, now); }
      } else {
        if (now >= st.nextBlink) { st.blinkEnd = now; st.nextBlink = now + rnd(2000, 3500); }
        draw(x, bob, mirror, { tilt: idleTilt, blink: blinkAt(now, st.blinkEnd) }, now);
        if (now >= st.until) {
          // Каждый третий-четвёртый раз — бьётся лбом в экран, иначе перелетает на другую сторону.
          if (++hovers % 3 === 0 && Math.random() < 0.8) st = { kind: 'bonk', t0: now };
          else if (maxX - minX > 40) {
            const half = (minX + maxX) / 2;
            const to = x > half ? rnd(minX, minX + (half - minX) * 0.8) : rnd(half + (maxX - half) * 0.2, maxX);
            mirror = to < x; // разворот — мгновенное зеркало (#2352)
            st = { kind: 'fly', t0: now, ms: Math.max(1800, (Math.abs(to - x) / 200) * 1000), from: x, to };
          } else st = startHover(now);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    draw(x, y0, mirror);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fw, height, reduce]);

  return (
    <div ref={boxRef} aria-hidden className={cn('pointer-events-none relative w-full overflow-hidden', className)}
      style={{ height: Math.round(height * 1.35) }}>
      <div ref={botRef} className="absolute left-0 top-0 will-change-transform"
        style={{ width: fw, height: Math.round(height * RIG_FH), transformOrigin: `${RIG_BODY_C * 100}% 50%` }}>
        <RobotParts />
      </div>
    </div>
  );
}

export default WaitingRobot;
